'use client';

import {
  DETAIL_ANIMAL_SEARCH_TYPES,
  SEARCH_TYPE,
} from '@/constants/searchType';
import { DATA_GRID_MODE } from '@/constants/dataGrid';
import { RESULT_DISPLAY_TYPE } from '@/constants/resultDisplay';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { BreedlogicMarkIcon, FilterIcon } from '@/components/icons';
import { GridLogicOperator } from '@mui/x-data-grid-pro';
import type {
  GridCallbackDetails,
  GridColumnOrderChangeParams,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridRenderCellParams,
  GridRowId,
  GridSortModel,
  GridValidRowModel,
} from '@mui/x-data-grid-pro';
import { closeSnackbar } from '@/providers';
import { useAnimalDisplayType } from '@/hooks/publicSearch/animal/useAnimalDisplayType';
import useFilterStore from '@/publicSearch/store/useFilterStore';
import useGridStore from '@/publicSearch/store/useGridStore';
import {
  getSearchPagination,
  SALE_LOT_PAGE_SIZE_OPTIONS,
} from '@/lib/searchPagination';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import { useSearch } from '@/hooks/publicSearch/useSearch';
import { useSaleIdParam } from '@/hooks/publicSearch/useSaleIdParam';
import useSearchStore from '@/publicSearch/store/useSearchStore';
import { useSearchType } from '@/hooks/publicSearch/useSearchType';
import { useIsEbvEnabled } from '@/hooks/publicSearch/useIsEbvEnabled';
import { useEbvDisplayOptions } from '@/hooks/publicSearch/useEbvDisplayOptions';
import { abortAllPendingGraphQL } from '@/publicSearch/apolloClient';
import PublicSearchDataGrid from '../searchSection/PublicSearchDataGrid';
import PublicSearchGalleryView from '../searchSection/PublicSearchGalleryView';
import { DeferredSaleLotList } from '../searchSection/DeferredSaleLotList';
import { TraitSettingsProvider } from '../searchSection/TraitSettingsContext';
import { getVisibleFieldKeysFromAnimalSearchConfig } from '@/hooks/publicSearch/animal/animalEbvGrid';
import { getVisibleMemberFieldKeys } from '@/hooks/publicSearch/member/memberGrid';
import {
  EBV_INDEX_CATEGORY,
  ebvGroupingOptionsFromDisplay,
  getEbvConfigGroupedByCategory,
  getEbvVisibilityChanges,
  parseEbvConfig,
} from '@/hooks/publicSearch/ebv/ebvColumns';
import { debounce } from 'lodash';

function sortModelsEqual(a: GridSortModel, b: GridSortModel): boolean {
  if (a.length !== b.length) return false;
  return a.every(
    (item, index) =>
      item.field === b[index]?.field && item.sort === b[index]?.sort,
  );
}

import {
  AnimalDefaultFieldOrder,
  FieldOption,
  LogicalOperator,
  MemberDefaultFieldOrder,
  SaleDefaultFieldOrder,
  SaleLotsDefaultFieldOrder,
} from '../constants';
import { Skeleton } from '@/primitives';

/**
 * Skeleton placeholder matching the mobile list-row card layout. The row height
 * and the per-line text widths are parameterized so the same structure serves
 * the animal, sale and member rows.
 */
function ListRowSkeleton({
  height,
  textLines,
}: {
  height: number;
  textLines: { width: string; height: number }[];
}) {
  return (
    <Box
      sx={(t) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        width: '100%',
        height,
        px: '14px',
        boxSizing: 'border-box',
        borderTop: `1px solid ${t.palette.divider}`,
      })}
    >
      <Skeleton
        variant="circular"
        width={28}
        height={28}
        sx={{ flexShrink: 0 }}
      />
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
        }}
      >
        {textLines.map((line, index) => (
          <Skeleton
            key={index}
            variant="text"
            width={line.width}
            height={line.height}
          />
        ))}
      </Box>
      <Skeleton
        variant="rounded"
        width={16}
        height={16}
        sx={{ flexShrink: 0 }}
      />
    </Box>
  );
}

/** Skeleton placeholder matching the AnimalListRow mobile card layout. */
function AnimalListRowSkeleton() {
  return (
    <ListRowSkeleton
      height={60}
      textLines={[
        { width: '52%', height: 16 },
        { width: '76%', height: 13 },
      ]}
    />
  );
}

/** Skeleton placeholder matching the SaleListRow mobile card layout. */
function SaleListRowSkeleton() {
  return (
    <ListRowSkeleton
      height={60}
      textLines={[
        { width: '60%', height: 16 },
        { width: '40%', height: 13 },
      ]}
    />
  );
}

/** Skeleton placeholder matching the MemberListRow card layout. */
function MemberListRowSkeleton() {
  return (
    <ListRowSkeleton
      height={76}
      textLines={[
        { width: '48%', height: 16 },
        { width: '65%', height: 13 },
        { width: '52%', height: 13 },
      ]}
    />
  );
}

/**
 * The one column the compact animal and lot lists show. Society column config
 * may omit Animal Name (e.g. AUBW uses a SocietyAnimalField "Pedigree Name"
 * instead); falling back to the first column keeps the compact list from
 * hiding every column.
 */
function compactAnimalListField(columns: { field: string }[]): string {
  return columns.some((c) => c.field === FieldOption.animalName.value)
    ? FieldOption.animalName.value
    : (columns[0]?.field ?? '');
}

function singleColumnVisibility(
  columns: { field: string }[],
  primaryField: string,
): GridColumnVisibilityModel {
  const model: GridColumnVisibilityModel = {};
  columns.forEach((col) => {
    model[col.field] = col.field === primaryField;
  });
  return model;
}

export default function SearchResult() {
  const { resultDisplay } = useAnimalDisplayType();
  const {
    openResult,
    setOpenResult,
    setBasicSearch,
    setOpenBasicSearchDialog,
    openBasicSearchDialog,
    setIsAiMode,
    setAiSearchPrompt,
  } = useSearchStore();
  const {
    columnOrder,
    columnVisibilityModel,
    ebvCategoryVisibility,
    ebvColumnVisibility,
    gridSortModel,
    manualColumnView,
    paginationModel: storedPaginationModel,
    setColumnVisibilityModel,
    setEbvCategoryVisibility,
    setEbvColumnVisibility,
    setColumnOrder,
    setGridSortModel,
    setManualColumnView,
    setPaginationModel,
    setShowEBVs,
    showEBVs,
    showEBVAccuracy,
    showPercentiles,
  } = useGridStore();
  const society = usePublicSearchContext((c) => c.society);
  const isEbvEnabled = useIsEbvEnabled();
  const { useColourSlotGrouping } = useEbvDisplayOptions();
  const ebvGroupingOptions = useMemo(
    () => ebvGroupingOptionsFromDisplay({ useColourSlotGrouping }),
    [useColourSlotGrouping],
  );
  const searchResult = useSearch();
  const { data, dataGrid, loading, previousData, startSearch } = searchResult;
  const ebvTraitExplanationPopover =
    'ebvTraitExplanationPopover' in searchResult
      ? searchResult.ebvTraitExplanationPopover
      : null;
  const searchType = useSearchType();
  const paginationModel = getSearchPagination(
    storedPaginationModel,
    searchType,
  );
  const showAllLots =
    DETAIL_ANIMAL_SEARCH_TYPES.includes(searchType) &&
    paginationModel.pageSize === -1;
  const saleId = useSaleIdParam();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // ALL lots on a phone or tablet: DeferredSaleLotList lists them in the
  // page's own scroll (see the list view below).
  const listsAllOnPage = isMobile && showAllLots;
  const {
    commonFilterOperator,
    filterGroups,
    filters,
    clearFilter: clearSavedFilter,
  } = useFilterStore(searchType);
  const [gridFilterModel, setGridFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  // The list the grid shows. Its scroll position is kept per key: coming back
  // to the same list restores it, and another page, filter or sale starts at
  // the top. Content-compared, so a store rewriting the same filters on mount
  // is not a change. The AND/OR operators stay out: with no filters they flip
  // on every mount (the URL sync and the quick search disagree on the
  // default) without changing the list.
  const scrollRestoreKey = useMemo(
    () =>
      JSON.stringify([
        searchType,
        saleId ?? null,
        paginationModel.page,
        paginationModel.pageSize,
        filters,
        filterGroups,
      ]),
    [
      filterGroups,
      filters,
      paginationModel.page,
      paginationModel.pageSize,
      saleId,
      searchType,
    ],
  );
  const normalizeField = (f: string) => f.replace(/_/g, '').toLowerCase();
  const filteredFields = useMemo(
    () =>
      filters
        .filter(({ field, operator, value }) => field && (operator || value))
        .map(({ field }) => normalizeField(field)),
    [filters],
  );
  const columnFieldSet = useMemo(
    () => new Set(dataGrid?.columns?.map((col) => col.field) ?? []),
    [dataGrid?.columns],
  );

  const clearFilter = () => {
    closeSnackbar && closeSnackbar();
    // Cancel any in-flight GraphQL queries before resetting filters/UI
    abortAllPendingGraphQL();
    setIsAiMode(false);
    setAiSearchPrompt('');
    setBasicSearch('');
    setGridSortModel([]);
    clearSavedFilter();
    // Animal searches derive their welcome state from the remaining criteria.
    setOpenResult(true);
  };

  // What the grid is told the server has. It is 0 on every mount, until the
  // first response lands.
  const gridRowCount = loading
    ? (previousData?.totalCount ?? 0)
    : (data?.totalCount ?? 0);

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      // DataGridPro can invoke this while reconciling controlled sortModel during
      // render (e.g. column set changes on sale/lot grids). Defer store writes
      // so header subscribers are not updated mid-render.
      if (loading && model.length === 0) return;

      queueMicrotask(() => {
        const current = useGridStore.getState().gridSortModel;
        if (sortModelsEqual(model, current)) return;
        setGridSortModel(model);
        startSearch();
      });
    },
    [loading, setGridSortModel, startSearch],
  );

  // Apply stored column order
  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- deps already match the values read (dataGrid?.columns, columnOrder, manualColumnView); the compiler cannot preserve this memo because the dependency is reached via optional chaining on the `dataGrid` object returned by useSearch()
  const orderedColumns = useMemo(() => {
    if (
      !dataGrid?.columns ||
      dataGrid.columns.length === 0 ||
      !manualColumnView ||
      !columnOrder.length
    ) {
      return dataGrid?.columns;
    }

    // Create a copy of columns
    const columns = [...(dataGrid.columns ?? [])];
    const orderedCols: typeof columns = [];

    // First, add columns in the stored order
    columnOrder.forEach((fieldName) => {
      const column = columns.find((col) => col.field === fieldName);
      if (column) {
        orderedCols.push(column);
      }
    });

    // Then add any remaining columns that weren't in the stored order
    columns.forEach((column) => {
      if (!columnOrder.includes(column.field)) {
        orderedCols.push(column);
      }
    });

    return orderedCols;
  }, [dataGrid?.columns, columnOrder, manualColumnView]);

  const columnsWithFilterIndicator = useMemo(() => {
    const baseColumns = orderedColumns ?? [...(dataGrid?.columns ?? [])];
    return baseColumns.map((column) => ({
      ...column,
      renderHeader: (params: any) => {
        const headerContent =
          'renderHeader' in column && typeof column.renderHeader === 'function'
            ? column.renderHeader(params)
            : (column.headerName ?? column.field);
        const norm = normalizeField(column.field);
        const isFiltered = filteredFields.some((f) => f.startsWith(norm));
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              gap: 0.5,
              width: '100%',
              color: isFiltered ? 'primary.main' : 'text.primary',
            }}
          >
            {headerContent}
          </Box>
        );
      },
    }));
  }, [dataGrid?.columns, filteredFields, orderedColumns]);

  // DeferredSaleLotList draws each lot with the compact list's own cell, so
  // its rows (and the breed-average row) look as they do in the grid.
  const compactLotColumn = useMemo(() => {
    const field = compactAnimalListField(columnsWithFilterIndicator);
    return columnsWithFilterIndicator.find((column) => column.field === field);
  }, [columnsWithFilterIndicator]);
  const getSearchRowId = dataGrid?.getRowId as
    ((row: GridValidRowModel) => GridRowId) | undefined;
  const getCompactLotRowKey = useCallback(
    (row: GridValidRowModel, index: number) =>
      String(getSearchRowId?.(row) ?? index),
    [getSearchRowId],
  );
  const renderCompactLotRow = useCallback(
    (row: GridValidRowModel) => {
      if (!compactLotColumn) return null;
      const value = row[compactLotColumn.field];
      if (!compactLotColumn.renderCell) return String(value ?? '');
      return compactLotColumn.renderCell({
        row,
        id: getCompactLotRowKey(row, 0),
        field: compactLotColumn.field,
        value,
      } as GridRenderCellParams);
    },
    [compactLotColumn, getCompactLotRowKey],
  );

  const handleFilterModelChange = useMemo(
    () =>
      debounce(
        (_model: GridFilterModel, details: GridCallbackDetails<'filter'>) => {
          if (!details.reason) return;
          setOpenBasicSearchDialog(true);
        },
        500,
      ),
    [setOpenBasicSearchDialog],
  );

  // 2 way binding between data grid filter model and animal filter store (skip when basic search dialog is open to avoid grid resizing)
  useEffect(() => {
    if (openBasicSearchDialog) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- two-way binding that mirrors the filter store into the grid filter model, but is intentionally skipped while the basic-search dialog is open (to avoid grid resizing); deriving it would update mid-dialog and change behavior
    setGridFilterModel({
      items: filters
        .filter(({ field }) => field && columnFieldSet.has(field))
        .map(({ field, operator, value }, index) => ({
          field,
          id: `${index}:${field}:${operator}:${value}`,
          operator,
          value,
        })),
      logicOperator:
        commonFilterOperator === LogicalOperator.and
          ? GridLogicOperator.And
          : GridLogicOperator.Or,
    });
  }, [columnFieldSet, commonFilterOperator, filters, openBasicSearchDialog]);

  // Memoize column field names to create a stable dependency
  const columnFields = useMemo(
    () => dataGrid?.columns?.map((col) => col.field).join(',') || '',
    [dataGrid?.columns],
  );

  const ebvConfig = useMemo(() => {
    if (!isEbvEnabled) return [];
    return parseEbvConfig(society.settings);
  }, [isEbvEnabled, society.settings]);

  const ebvFieldToCategory = useMemo(() => {
    const grouped = getEbvConfigGroupedByCategory(
      ebvConfig,
      ebvGroupingOptions,
    );
    const map: Record<string, string> = {};
    for (const { category, items } of grouped) {
      for (const item of items) {
        if (!item?.field) continue;
        const n = item.field.replace('ebv', '');
        map[`EBV${n}`] = category;
      }
    }
    return map;
  }, [ebvConfig, ebvGroupingOptions]);

  const animalSearchVisibleFieldKeys = useMemo(
    () => getVisibleFieldKeysFromAnimalSearchConfig(society.settings),
    [society.settings],
  );

  const memberSearchVisibleFieldKeys = useMemo(
    () => getVisibleMemberFieldKeys(society.settings),
    [society.settings],
  );
  const isCompactAnimalList =
    resultDisplay === RESULT_DISPLAY_TYPE.LIST &&
    searchType === SEARCH_TYPE.ANIMAL &&
    isMobile;
  const isCompactMemberList =
    resultDisplay === RESULT_DISPLAY_TYPE.LIST &&
    searchType === SEARCH_TYPE.MEMBERS &&
    isMobile;
  const isCompactSaleList =
    resultDisplay === RESULT_DISPLAY_TYPE.LIST &&
    searchType === SEARCH_TYPE.SALE_CATALOGUES &&
    isMobile;
  const isCompactSaleLotList =
    resultDisplay === RESULT_DISPLAY_TYPE.LIST &&
    (searchType === SEARCH_TYPE.SALE_CATALOGUES_DETAIL_ANIMALS ||
      searchType === SEARCH_TYPE.SEMEN_CATALOGUES_DETAIL_ANIMALS) &&
    isMobile;
  // Desktop detail-animal table carries the Images column; give its rows extra
  // height so the thumbnail isn't cropped top/bottom.
  const isSaleLotDetailTable =
    DETAIL_ANIMAL_SEARCH_TYPES.includes(searchType) && !isCompactSaleLotList;

  const gridRowHeight = useMemo(() => {
    if (isCompactMemberList) return 76;
    if (isCompactAnimalList) return 60;
    // Taller rows so the Images-column thumbnail isn't cropped.
    if (isSaleLotDetailTable) return 76;
    return 44;
  }, [isCompactMemberList, isCompactAnimalList, isSaleLotDetailTable]);
  const mobileColumnVisibilityModel = useMemo(() => {
    const cols = dataGrid?.columns;
    if (!cols?.length) return columnVisibilityModel;
    if (isCompactAnimalList || isCompactSaleLotList) {
      return singleColumnVisibility(cols, compactAnimalListField(cols));
    }
    if (isCompactSaleList)
      return singleColumnVisibility(cols, FieldOption.saleName.value);
    if (isCompactMemberList) {
      const primaryField = cols.some(
        (c) => c.field === FieldOption.memberName.value,
      )
        ? FieldOption.memberName.value
        : (cols[0]?.field ?? '');
      return singleColumnVisibility(cols, primaryField);
    }
    return columnVisibilityModel;
  }, [
    columnVisibilityModel,
    dataGrid?.columns,
    isCompactAnimalList,
    isCompactMemberList,
    isCompactSaleList,
    isCompactSaleLotList,
  ]);

  // Reset manual column view if current columns don't match stored visibility model
  // This handles cases where society settings change the available columns
  useEffect(() => {
    if (!manualColumnView || !dataGrid?.columns?.length) return;

    const currentColumnFields = new Set(
      dataGrid.columns.map((col) => col.field),
    );
    const storedVisibleFields = Object.keys(columnVisibilityModel).filter(
      (key) => columnVisibilityModel[key],
    );

    // If none of the stored visible fields exist in current columns, reset manual view
    const hasMatchingColumns = storedVisibleFields.some((field) =>
      currentColumnFields.has(field),
    );

    if (!hasMatchingColumns && storedVisibleFields.length > 0) {
      setManualColumnView(false);
    }
  }, [
    columnVisibilityModel,
    dataGrid?.columns,
    manualColumnView,
    setManualColumnView,
  ]);

  useEffect(() => {
    if (!dataGrid?.columns?.length) return;

    const showEbvColumn = (field: string) => {
      if (!field.startsWith('EBV')) return false;
      const category = ebvFieldToCategory[field];
      return !(category != null && ebvCategoryVisibility[category] === false);
    };

    // Default visibility for trait columns follows any EBV / Accuracy /
    // Percentile display toggle. Index columns carry only headline EBV values,
    // so they are visible only while the EBV toggle is on.
    const applyDefaultEbvVisibility = (target: GridColumnVisibilityModel) => {
      if (!isEbvEnabled) return;
      const ebvOn = showEBVs || showEBVAccuracy || showPercentiles;
      if (!ebvOn) return;
      for (const col of dataGrid.columns) {
        if (!col.field.startsWith('EBV') || !showEbvColumn(col.field)) continue;
        const isIndexField = ebvFieldToCategory[col.field] === 'Indexes';
        target[col.field] = isIndexField ? showEBVs : true;
      }
    };

    const searchTypesWithEbvColumns: string[] = [
      SEARCH_TYPE.ANIMAL,
      ...DETAIL_ANIMAL_SEARCH_TYPES,
    ];

    if (manualColumnView) {
      if (!searchTypesWithEbvColumns.includes(searchType)) {
        return;
      }
      const ebvOn =
        isEbvEnabled && (showEBVs || showEBVAccuracy || showPercentiles);
      const prev = useGridStore.getState().columnVisibilityModel;
      const next: GridColumnVisibilityModel = { ...prev };
      for (const col of dataGrid.columns) {
        if (!col.field.startsWith('EBV')) continue;
        // Trait columns are toggleable from Manage columns / Trait settings
        // independently of the display toggles. Indexes remain hard-gated by
        // the EBV toggle because they have no accuracy/percentile sub-values.
        const isIndexField = ebvFieldToCategory[col.field] === 'Indexes';
        const hasIntent = Object.prototype.hasOwnProperty.call(
          ebvColumnVisibility,
          col.field,
        );
        const desired = hasIntent
          ? ebvColumnVisibility[col.field] !== false
          : ebvOn;
        // Hard gate: when the society has EBVs disabled, no EBV column may show,
        // regardless of any persisted per-trait intent.
        next[col.field] =
          isEbvEnabled &&
          desired &&
          (!isIndexField || showEBVs) &&
          showEbvColumn(col.field);
      }
      setColumnVisibilityModel(next);
      return;
    }

    const colViewModel: GridColumnVisibilityModel = {};

    // hide all columns by default, then enable defaults for the search type
    dataGrid.columns.forEach((col) => {
      colViewModel[col.field] = false;
    });

    if (searchType === SEARCH_TYPE.ANIMAL) {
      const animalFieldKeys =
        animalSearchVisibleFieldKeys.length > 0
          ? animalSearchVisibleFieldKeys
          : AnimalDefaultFieldOrder.map(({ value }) => value);
      animalFieldKeys.forEach((fieldKey) => {
        if (dataGrid.columns.some((col) => col.field === fieldKey)) {
          colViewModel[fieldKey] = true;
        }
      });

      applyDefaultEbvVisibility(colViewModel);
    }

    if (searchType === SEARCH_TYPE.MEMBERS) {
      const memberFieldKeys =
        memberSearchVisibleFieldKeys.length > 0
          ? memberSearchVisibleFieldKeys
          : MemberDefaultFieldOrder.map(({ value }) => value);
      memberFieldKeys.forEach((fieldKey) => {
        if (dataGrid.columns.some((col) => col.field === fieldKey)) {
          colViewModel[fieldKey] = true;
        }
      });
    }

    if (searchType === SEARCH_TYPE.SALE_CATALOGUES) {
      SaleDefaultFieldOrder.forEach(({ value }) => {
        colViewModel[value] = true;
      });
    }
    if (DETAIL_ANIMAL_SEARCH_TYPES.includes(searchType)) {
      SaleLotsDefaultFieldOrder.forEach(({ value }) => {
        colViewModel[value] = true;
      });
      applyDefaultEbvVisibility(colViewModel);
    }
    setColumnVisibilityModel(colViewModel);
  }, [
    columnFields,
    animalSearchVisibleFieldKeys,
    memberSearchVisibleFieldKeys,
    dataGrid?.columns,
    ebvCategoryVisibility,
    ebvColumnVisibility,
    ebvFieldToCategory,
    manualColumnView,
    searchType,
    setColumnVisibilityModel,
    showEBVs,
    showEBVAccuracy,
    showPercentiles,
    isEbvEnabled,
  ]);

  const handleColumnVisibilityChange = (
    model: GridColumnVisibilityModel,
    _details: GridCallbackDetails,
  ) => {
    // Capture the model the grid is transitioning FROM (what the user currently
    // sees) before we overwrite it, so we can tell which column(s) they toggled.
    const prevModel = useGridStore.getState().columnVisibilityModel;
    const {
      columnVisibility,
      categoriesToShow,
      categoriesWithNewlyShownColumns,
    } = getEbvVisibilityChanges(
      dataGrid?.columns ?? [],
      prevModel,
      model,
      ebvFieldToCategory,
      ebvCategoryVisibility,
      showEBVs ? undefined : EBV_INDEX_CATEGORY,
    );
    setManualColumnView(true);
    setColumnVisibilityModel(model);
    // Mirror EBV column visibility into the per-trait store so hiding/showing
    // an EBV (incl. index) column from the Manage columns panel persists and
    // isn't re-derived by the recompute effect. Record intent ONLY for the
    // column(s) whose visibility actually changed: recording every EBV column
    // on each toggle would freeze the rest, so turning the EBV / Accuracy /
    // Percentile display toggles back on later wouldn't reveal them. The
    // exceptions are a category reopened from Manage columns, and an index
    // selection that auto-enables EBVs. Those sync the relevant panel checkbox
    // state so previously selected columns do not all reappear.
    setEbvColumnVisibility((prev) => ({ ...prev, ...columnVisibility }));
    if (
      !showEBVs &&
      categoriesWithNewlyShownColumns.includes(EBV_INDEX_CATEGORY)
    ) {
      // An index cannot display while EBVs are off. Treat selecting one from
      // Manage columns as an explicit request to enable EBVs, while retaining
      // the hard gate when the user switches EBVs off.
      setShowEBVs(true);
    }
    if (categoriesToShow.length > 0) {
      setEbvCategoryVisibility((prev) => {
        const next = { ...prev };
        for (const category of categoriesToShow) {
          next[category] = true;
        }
        return next;
      });
    }
  };

  const handleColumnOrderChange = useCallback(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization -- depends on `orderedColumns`, a memo the compiler can't preserve (optional-chained `dataGrid?.columns` dep); deps here are correct and complete
    (params: GridColumnOrderChangeParams) => {
      // Use the current columns and apply the reorder based on the params
      if (!dataGrid?.columns || dataGrid.columns.length === 0) return;

      const { oldIndex, targetIndex } = params;
      const currentColumns = orderedColumns || dataGrid.columns;
      const newOrder = [...currentColumns];

      // Move the column from oldIndex to targetIndex
      const movedColumn = newOrder.splice(oldIndex, 1)[0]!;
      newOrder.splice(targetIndex, 0, movedColumn);

      // Extract the field names in the new order
      const columnFieldOrder = newOrder.map((col) => col.field);

      setColumnOrder(columnFieldOrder);
      setManualColumnView(true);
    },
    [dataGrid?.columns, orderedColumns, setColumnOrder, setManualColumnView],
  );

  if (searchResult.showSearchWelcome) {
    const isMemberSearch = searchType === SEARCH_TYPE.MEMBERS;
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
        }}
      >
        <BreedlogicMarkIcon
          aria-hidden
          sx={{ fontSize: 170, color: 'primary.main', opacity: 0.1 }}
        />
        <Typography
          color="text.secondary"
          sx={{ mt: 3, maxWidth: 560, textAlign: 'center' }}
        >
          {isMemberSearch
            ? 'To get started, enter a member name, ident or herd prefix in the quick search bar, or use the filter '
            : 'To get started, enter an animal name or identifier in the quick search bar, or use the filter '}
          <FilterIcon
            aria-hidden
            sx={{ fontSize: 18, verticalAlign: 'text-bottom', mx: 0.25 }}
          />
          {isMemberSearch
            ? ' button to search members.'
            : ' button to search animals.'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: openResult ? 'flex' : 'none',
        flexDirection: 'column',
        flex: 1,
        // Below md the results take their natural height and the page scrolls
        // (see the public-search layout); above it they fill the space left
        // and the grid or gallery scrolls inside it.
        minHeight: { xs: 'auto', md: 0 },
        bgcolor: 'background.default',
        overflow: { xs: 'visible', md: 'hidden' },
      }}
    >
      {showAllLots && searchResult.error && (
        <Alert
          severity="error"
          sx={{ flexShrink: 0, mb: 1 }}
          action={
            <Button color="inherit" onClick={startSearch}>
              Retry
            </Button>
          }
        >
          Could not load all lots. Please try again.
        </Alert>
      )}
      {!dataGrid?.columns?.length ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            mb: 5,
            paddingBottom: '100px',
          }}
        >
          <Typography variant="titlesmall">No columns to display</Typography>
        </Box>
      ) : (
        <>
          {resultDisplay === RESULT_DISPLAY_TYPE.LIST && openResult && (
            <TraitSettingsProvider>
              <Box
                sx={{
                  width: '100%',
                  flex: 1,
                  minHeight: { xs: 'auto', md: 0 },
                  display: 'flex',
                  flexDirection: 'column',
                  ...(listsAllOnPage && {
                    // Kept mounted but out of sight and out of the layout:
                    // export and print read the lots through the grid's API
                    // (searchApiRef). DeferredSaleLotList below shows them.
                    height: 0,
                    minHeight: 0,
                    flex: 'none',
                    overflow: 'hidden',
                    visibility: 'hidden',
                  }),
                  // `clip`, not `hidden`: a hidden x-axis turns y into `auto`,
                  // which gave the compact sale lists a second scrollbar
                  // beside the grid's whenever the space was short.
                  ...((isCompactSaleList || isCompactSaleLotList) && {
                    overflowX: 'clip',
                  }),
                }}
              >
                <PublicSearchDataGrid
                  // Large catalogues need a bounded grid to stay virtualized.
                  // On phones and tablets a bounded grid scrolled inside the
                  // page, so with ALL it is hidden and the lots are listed
                  // below instead; numbered pages grow the grid to fit.
                  autoHeight={isMobile && !showAllLots}
                  pageSizeOptions={
                    DETAIL_ANIMAL_SEARCH_TYPES.includes(searchType)
                      ? SALE_LOT_PAGE_SIZE_OPTIONS
                      : undefined
                  }
                  columns={columnsWithFilterIndicator}
                  columnGroupingModel={
                    'columnGroupingModel' in dataGrid
                      ? dataGrid.columnGroupingModel
                      : undefined
                  }
                  columnHeaderHeight={
                    isCompactAnimalList ||
                    isCompactMemberList ||
                    isCompactSaleList ||
                    isCompactSaleLotList
                      ? 0
                      : 48
                  }
                  columnVisibilityModel={mobileColumnVisibilityModel}
                  filterDebounceMs={500}
                  filterMode={DATA_GRID_MODE.SERVER}
                  filterModel={gridFilterModel}
                  getRowId={dataGrid.getRowId as any}
                  initialState={{
                    columns: {
                      columnVisibilityModel: mobileColumnVisibilityModel,
                    },
                  }}
                  loading={loading}
                  pagination
                  paginationModel={paginationModel}
                  rowCount={gridRowCount}
                  rowHeight={gridRowHeight}
                  {...(isCompactSaleList || isCompactSaleLotList
                    ? { getRowHeight: () => 'auto' as const }
                    : {})}
                  rows={dataGrid.rows ?? []}
                  scrollRestoreKey={
                    listsAllOnPage ? undefined : scrollRestoreKey
                  }
                  sx={[
                    ...(isCompactAnimalList ||
                    isCompactMemberList ||
                    isCompactSaleList ||
                    isCompactSaleLotList
                      ? [
                          {
                            '& .MuiDataGrid-columnHeaders': { display: 'none' },
                            '& .MuiDataGrid-cell': {
                              padding: 0,
                              borderBottom: 'none',
                            },
                            '& .MuiDataGrid-row:hover': {
                              backgroundColor: 'transparent',
                            },
                            '&.MuiDataGrid-root::before': {
                              display: 'none',
                            },
                          },
                        ]
                      : []),
                    ...(isCompactAnimalList || isCompactMemberList
                      ? [
                          {
                            '& .MuiDataGrid-row': {
                              minHeight: isCompactMemberList
                                ? '76px !important'
                                : '60px !important',
                              maxHeight: isCompactMemberList
                                ? '76px !important'
                                : '60px !important',
                            },
                          },
                        ]
                      : []),
                    ...(isCompactAnimalList
                      ? [
                          {
                            '& .MuiDataGrid-virtualScroller': {
                              paddingBottom: '16px',
                            },
                          },
                        ]
                      : []),
                    ...(isCompactSaleList || isCompactSaleLotList
                      ? [
                          {
                            // Shared grid chrome sets a fixed 44px height.
                            // Let MUI measure the full compact card, including
                            // wrapped names and the sale result line.
                            '& .MuiDataGrid-row.MuiDataGrid-row--dynamicHeight':
                              {
                                height: 'auto',
                              },
                            '& .MuiDataGrid-virtualScroller': {
                              overflowX: 'hidden',
                            },
                            '& .MuiDataGrid-cell': {
                              whiteSpace: 'normal',
                              overflow: 'visible',
                              alignItems: 'flex-start',
                            },
                          },
                        ]
                      : []),
                  ]}
                  slots={{
                    noRowsOverlay: () => (
                      <Button color="warning" onClick={() => clearFilter()}>
                        Clear search
                      </Button>
                    ),
                    ...((isCompactAnimalList || isCompactSaleLotList) && {
                      skeletonCell: AnimalListRowSkeleton,
                    }),
                    ...(isCompactMemberList && {
                      skeletonCell: MemberListRowSkeleton,
                    }),
                    ...(isCompactSaleList && {
                      skeletonCell: SaleListRowSkeleton,
                    }),
                  }}
                  sortModel={gridSortModel}
                  onColumnOrderChange={handleColumnOrderChange}
                  onColumnVisibilityModelChange={handleColumnVisibilityChange}
                  onFilterModelChange={handleFilterModelChange}
                  onPaginationModelChange={setPaginationModel}
                  onSortModelChange={handleSortModelChange}
                />
              </Box>
              {listsAllOnPage && (
                <DeferredSaleLotList
                  rows={dataGrid.rows ?? []}
                  getRowKey={getCompactLotRowKey}
                  renderRow={renderCompactLotRow}
                  loading={loading}
                  rowCount={gridRowCount}
                  paginationModel={paginationModel}
                  pageSizeOptions={SALE_LOT_PAGE_SIZE_OPTIONS}
                  onPaginationModelChange={setPaginationModel}
                  skeleton={<AnimalListRowSkeleton />}
                  noRows={
                    <Button color="warning" onClick={() => clearFilter()}>
                      Clear search
                    </Button>
                  }
                  scrollRestoreKey={scrollRestoreKey}
                />
              )}
            </TraitSettingsProvider>
          )}
          {resultDisplay === RESULT_DISPLAY_TYPE.GALLERY && (
            <Box
              sx={{
                width: '100%',
                flex: 1,
                minHeight: { xs: 'auto', md: 0 },
                display: 'flex',
                flexDirection: 'column',
                overflow: { xs: 'visible', md: 'auto' },
              }}
            >
              <PublicSearchGalleryView
                // Same key as the grid: on a reload the list view renders
                // once before the saved card view replaces it, and a view in
                // the key would reset the cards' position on that render.
                scrollRestoreKey={scrollRestoreKey}
                pageScrolls={isMobile}
              />
            </Box>
          )}
        </>
      )}
      {ebvTraitExplanationPopover}
    </Box>
  );
}
